/**
 * /customer/settings/account/delete — Multi-step destructive flow
 * (W-Apple).
 *
 * Steps:
 *   1. Warning (what will happen)
 *   2. Confirmation checkboxes + optional reason
 *   3. Final: type "DELETE" + password + Cancel|Delete
 *   4. Success: confirmation + auto-logout (5s)
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, ShieldAlert, AlertTriangle, Loader2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

const REASONS = [
  '',
  "I don't use OneStamp anymore",
  'Privacy concerns',
  'Too many notifications',
  'Found a better app',
  'Other',
];

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const signOut = useAuthStore((s) => s.signOut);

  const [step, setStep] = useState(1); // 1=warning · 2=confirm · 3=final · 4=success
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [check3, setCheck3] = useState(false);
  const [reason, setReason] = useState('');
  const [typedConfirm, setTypedConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [finalizeAt, setFinalizeAt] = useState(null);

  const allChecked = check1 && check2 && check3;
  const finalReady = typedConfirm === 'DELETE' && password.length >= 6 && !submitting;

  const onSubmit = async () => {
    if (!finalReady) return;
    setSubmitting(true);
    try {
      const res = await api.requestAccountDeletion(token, {
        password,
        reason: reason || null,
      });
      if (res?.code === 'invalid_password') {
        toast.error('Incorrect password');
        setSubmitting(false);
        return;
      }
      if (res?.status === 'pending') {
        setFinalizeAt(res.finalize_at);
        setStep(4);
      } else {
        toast.error(res?.code || 'Could not process deletion request');
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e?.detail || 'Network error');
      setSubmitting(false);
    }
  };

  // Auto-logout 5s after success
  useEffect(() => {
    if (step !== 4) return;
    const t = setTimeout(() => {
      signOut();
      navigate('/customer/login', { replace: true });
    }, 5000);
    return () => clearTimeout(t);
  }, [step, signOut, navigate]);

  return (
    <div className="space-y-6 max-w-xl" data-testid="customer-delete-account-screen">
      <header className="flex items-center gap-3">
        {step < 4 ? (
          <Link
            to="/customer/settings/account"
            className="p-2 -ml-2 rounded-full hover:bg-white/5 text-white/70"
            aria-label="Back to account settings"
            data-testid="delete-back"
          >
            <ArrowLeft size={20} />
          </Link>
        ) : null}
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
          Delete Account
        </h1>
      </header>

      {step === 1 ? <Step1Warning onContinue={() => setStep(2)} /> : null}

      {step === 2 ? (
        <Step2Confirm
          check1={check1} setCheck1={setCheck1}
          check2={check2} setCheck2={setCheck2}
          check3={check3} setCheck3={setCheck3}
          reason={reason} setReason={setReason}
          canContinue={allChecked}
          onContinue={() => setStep(3)}
        />
      ) : null}

      {step === 3 ? (
        <Step3Final
          typedConfirm={typedConfirm} setTypedConfirm={setTypedConfirm}
          password={password} setPassword={setPassword}
          submitting={submitting}
          finalReady={finalReady}
          onCancel={() => navigate('/customer/settings/account')}
          onDelete={onSubmit}
        />
      ) : null}

      {step === 4 ? <Step4Success finalizeAt={finalizeAt} /> : null}
    </div>
  );
}

// -- Step 1 -----------------------------------------------------------
function Step1Warning({ onContinue }) {
  return (
    <section className="rounded-2xl border border-red-500/20 bg-red-500/[0.04] p-5 space-y-4">
      <div className="flex items-start gap-3">
        <ShieldAlert className="text-red-400 mt-0.5" size={22} />
        <div>
          <h2 className="text-white font-semibold">
            You're about to delete your OneStamp account.
          </h2>
          <p className="mt-1 text-sm text-white/60">
            Please read carefully before continuing.
          </p>
        </div>
      </div>

      <ul className="space-y-2 text-sm text-white/80 pl-4 list-disc">
        <li>You'll be signed out immediately on all your devices.</li>
        <li>Your stamps, rewards, and points history will be erased.</li>
        <li>
          Your transaction record stays in our books for tax and legal
          reasons — but it will no longer be linked to you.
        </li>
        <li>
          You have <span className="text-[#D4AF37] font-semibold">30 days</span>
          {' '}to change your mind. After that, deletion is permanent.
        </li>
        <li>We'll send a confirmation email with a "Cancel deletion" link.</li>
      </ul>

      <button
        onClick={onContinue}
        data-testid="delete-step1-continue"
        className="w-full mt-2 rounded-xl border border-red-500/30 px-4 py-3 text-red-400 hover:bg-red-500/[0.08] transition font-medium"
      >
        Continue
      </button>
    </section>
  );
}

// -- Step 2 -----------------------------------------------------------
function Step2Confirm({
  check1, setCheck1, check2, setCheck2, check3, setCheck3,
  reason, setReason, canContinue, onContinue,
}) {
  return (
    <section className="rounded-2xl border border-white/8 bg-[#15151B] p-5 space-y-4">
      <h2 className="text-white font-semibold">Acknowledge the impact</h2>
      <CheckRow
        checked={check1} setChecked={setCheck1}
        testid="delete-check-1"
        label="I understand my stamps and rewards progress will be permanently lost."
      />
      <CheckRow
        checked={check2} setChecked={setCheck2}
        testid="delete-check-2"
        label="I understand this action cannot be reversed after 30 days."
      />
      <CheckRow
        checked={check3} setChecked={setCheck3}
        testid="delete-check-3"
        label="I understand I cannot create a new account with the same email immediately after."
      />

      <div className="pt-2">
        <label className="block text-sm text-white/60 mb-2">
          Reason for leaving (optional)
        </label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="delete-reason-select"
          className="w-full bg-[#0F0F14] border border-white/10 rounded-xl px-3 py-3 text-white/90 focus:border-[#D4AF37] outline-none"
        >
          {REASONS.map((r) => (
            <option key={r} value={r}>{r || '— Select —'}</option>
          ))}
        </select>
      </div>

      <button
        disabled={!canContinue}
        onClick={onContinue}
        data-testid="delete-step2-continue"
        className="w-full mt-2 rounded-xl border border-red-500/30 px-4 py-3 text-red-400 hover:bg-red-500/[0.08] transition font-medium disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Continue
      </button>
    </section>
  );
}

function CheckRow({ checked, setChecked, label, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => setChecked(e.target.checked)}
        data-testid={testid}
        className="mt-1 w-4 h-4 rounded border-white/30 bg-transparent accent-[#D4AF37]"
      />
      <span className="text-sm text-white/80">{label}</span>
    </label>
  );
}

// -- Step 3 -----------------------------------------------------------
function Step3Final({
  typedConfirm, setTypedConfirm, password, setPassword,
  submitting, finalReady, onCancel, onDelete,
}) {
  return (
    <section className="rounded-2xl border border-red-500/30 bg-red-500/[0.04] p-5 space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-red-400 mt-0.5" size={22} />
        <div>
          <h2 className="text-white font-semibold">Final confirmation</h2>
          <p className="mt-1 text-sm text-white/60">
            Type <code className="px-1.5 py-0.5 rounded bg-white/10 font-mono text-[#D4AF37]">DELETE</code> and enter your password.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm text-white/60 mb-2">
          Type DELETE to confirm
        </label>
        <input
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck="false"
          value={typedConfirm}
          onChange={(e) => setTypedConfirm(e.target.value)}
          data-testid="delete-type-confirm"
          placeholder="DELETE"
          className="w-full bg-[#0F0F14] border border-white/10 rounded-xl px-3 py-3 text-white font-mono tracking-wider focus:border-red-500 outline-none"
        />
      </div>

      <div>
        <label className="block text-sm text-white/60 mb-2">Password</label>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="delete-password"
          placeholder="••••••••"
          className="w-full bg-[#0F0F14] border border-white/10 rounded-xl px-3 py-3 text-white focus:border-[#D4AF37] outline-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2">
        <button
          onClick={onCancel}
          data-testid="delete-cancel-final"
          disabled={submitting}
          className="rounded-xl border border-white/15 px-4 py-3 text-white hover:bg-white/5 transition font-medium disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onDelete}
          disabled={!finalReady}
          data-testid="delete-confirm-final"
          className="rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold transition px-4 py-3 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
          Delete My Account
        </button>
      </div>
    </section>
  );
}

// -- Step 4 -----------------------------------------------------------
function Step4Success({ finalizeAt }) {
  const dateLabel = finalizeAt
    ? new Date(finalizeAt).toLocaleDateString('en-GB', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : 'in 30 days';
  return (
    <section
      className="rounded-2xl border border-white/8 bg-[#15151B] p-6 text-center"
      data-testid="delete-success-screen"
      role="status"
      aria-live="polite"
    >
      <CheckCircle2 size={40} className="text-[#D4AF37] mx-auto" />
      <h2 className="mt-3 text-xl text-white font-semibold">
        Your account is scheduled for deletion
      </h2>
      <p className="mt-2 text-sm text-white/70">
        We'll permanently delete your account on
        {' '}
        <span className="text-[#D4AF37]">{dateLabel}</span>.
      </p>
      <p className="mt-2 text-sm text-white/60">
        To cancel, sign in within the next 30 days.
      </p>
      <p className="mt-4 text-xs text-white/40">
        Signing you out in 5 seconds…
      </p>
    </section>
  );
}
